import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React from 'react';
import { useIntl } from 'react-intl';

import CampgroundX1 from '@/assets/become-a-host/campground/campgroundX1.png';
import CampgroundX2 from '@/assets/become-a-host/campground/campgroundX2.png';
import Renter from '@/assets/become-a-host/rv/renter.png';
import CamperVan from '@/assets/become-a-host/rv/van.png';
import Connection from '@/assets/become-a-host/stay/connection.jpg';
import Outdoor from '@/assets/become-a-host/stay/outdoor.jpg';
import { ERentalType } from '@/services/analytics/types';

import styles from './BecomeAHost.module.css';
import { useHostingStayFlow } from './Stay/useHostingStayFlow';
import { useHostingType } from './useHostingType';

export const HostExperiences = () => {
  const intl = useIntl();
  const { rentalType, isRentalStay, isRentalRV } = useHostingType();
  const { redirectToHostingStayFlow } = useHostingStayFlow();
  const content = {
    [ERentalType.RV]: {
      subTitle: intl.formatMessage({
        defaultMessage: 'Built for trust',
        id: 'EmFBXX',
      }),

      title: intl.formatMessage({
        defaultMessage: 'Support in all the right places',
        id: 'pYUz2w',
      }),

      items: [
        {
          title: intl.formatMessage({
            defaultMessage: 'We know who’s renting — you will too',
            id: 'TFUFp6',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Get booking requests from as close as your own community. And, occasionally, from as far away as across the globe. Leave the background checks to us.',
            id: 'rNrGLG',
          }),
          image: Renter,
          imageAlt: intl.formatMessage({ defaultMessage: 'renter', id: 'MSEqK5' }),
        },

        {
          title: intl.formatMessage({
            defaultMessage: 'Multipoint protection for your RV',
            id: 'qR8b0v',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Guests purchase a temporary insurance plan that covers your vehicle during their trip — up to $300,000 collision and comprehensive, roadside assistance, windshield coverage, and more. And, we have our own in-house claims team.',
            id: 'tOIGmO',
          }),
          image: CamperVan,
          imageAlt: intl.formatMessage({ defaultMessage: 'camper van', id: 'KMXAAV' }),
        },
      ],
    },
    [ERentalType.STAY]: {
      subTitle: intl.formatMessage({
        defaultMessage: 'Share your stay, grow your business',
        id: 'Zp1HvJ',
      }),

      title: intl.formatMessage({
        defaultMessage: 'Host outdoor enthusiasts just like you',
        id: 'd0AbgU',
      }),

      items: [
        {
          title: intl.formatMessage({
            defaultMessage: 'Helping guests bring the outside in',
            id: 'eerfex',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Coffee around the fire pit, not from the drive-through. Tall trees, not tall buildings. And, it’s called exploring, not getting lost. Let your stay help guests #liveoutdoorsy',
            id: 'ZHYMEL',
          }),
          image: Outdoor,
          imageAlt: intl.formatMessage({ defaultMessage: 'outdoor', id: '3oAicY' }),
        },

        {
          title: intl.formatMessage({
            defaultMessage: 'Make a meaningful connection',
            id: 'L+YFmy',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Share your favorite places to see, things to do, and experiences to remember so your guests can enjoy your stay as much as you.',
            id: '4htjvc',
          }),
          image: Connection,
          imageAlt: intl.formatMessage({ defaultMessage: 'connection', id: 'OjzRQo' }),
        },
      ],
    },

    [ERentalType.CAMPGROUND]: {
      subTitle: intl.formatMessage({
        defaultMessage: 'Built for trust',
        id: 'EmFBXX',
      }),

      title: intl.formatMessage({
        defaultMessage: 'Support in all the right places',
        id: 'pYUz2w',
      }),

      items: [
        {
          title: intl.formatMessage({
            defaultMessage: 'Partner with Outdoorsy for free',
            id: '1hDfKF',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Upon sign up, we’ll connect you with our customer service team to answer all your questions. From there, we will get your campground accommodations published on our marketplace.',
            id: 'ndx94u',
          }),
          image: CampgroundX1,
          imageAlt: intl.formatMessage({
            defaultMessage: 'Campground host experience image',
            id: 'bdkM7I',
          }),
        },

        {
          title: intl.formatMessage({
            defaultMessage: 'Seamless integration with your current PMS',
            id: '97cs8r',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Already have a Property Management System to manage your bookings? Great! Our platform seamlessly links to all your campground rates and availability, so you never have to duplicate work.',
            id: '/lxxSi',
          }),
          image: CampgroundX2,
          imageAlt: intl.formatMessage({
            defaultMessage: 'Campground host experience image',
            id: 'bdkM7I',
          }),
        },
      ],
    },
  };

  return (
    <section
      className={`${styles.spacing} text-center ${
        isRentalRV ? 'bg-white' : 'bg-primary-800 text-white'
      }`}>
      <Text
        variant={ETextStyleVariant.LegalRegular}
        className={`mb-4 uppercase ${!isRentalStay ? '' : 'text-red-300'}`}>
        {content[rentalType].subTitle}
      </Text>
      <Heading variant={EHeadingStyleVariant.H3} className="mb-12 md:mb-20">
        {content[rentalType].title}
      </Heading>

      {content[rentalType].items.map((item, idx) => (
        <div
          className={`flex flex-col-reverse ${
            idx % 2 === 0 ? 'md:flex-row' : 'mt-16 md:mt-24 md:flex-row-reverse'
          } md:items-center gap-8 md:gap-0`}
          key={item.title}>
          <div className="md:flex-1">
            <div className={`text-left ${idx % 2 === 0 ? 'md:mr-16' : 'md:ml-24'} md:max-w-28`}>
              <Heading variant={EHeadingStyleVariant.H4} className="mb-4">
                {item.title}
              </Heading>

              <Text variant={ETextStyleVariant.LargeRegular}>{item.description}</Text>
            </div>
          </div>

          <div className="relative w-full md:flex-1 md:h-80 sm:h-72 h-60">
            <Image
              alt={item.imageAlt}
              src={item.image}
              objectFit="cover"
              className="rounded-2xl"
              layout="fill"
            />
          </div>
        </div>
      ))}

      {isRentalStay && (
        <Button
          onClick={redirectToHostingStayFlow}
          variant={EButtonColorVariant.Primary}
          label={intl.formatMessage({ defaultMessage: 'Try hosting today', id: 'R7Xwt5' })}
          className="mt-16"
        />
      )}
    </section>
  );
};
